import {registerBrick} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import React from 'react';

const toEntries = (rawData) => {
    if (!rawData) {
        return [];
    }

    if (rawData instanceof Map) {
        return Array.from(rawData.entries());
    }

    if (typeof rawData === 'object') {
        return Object.entries(rawData);
    }

    return [];
};

const renderList = (title, values) => {
    if (!Array.isArray(values) || values.length === 0) {
        return (
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <span>{title}</span>
                    <span style={styles.countBadge}>0</span>
                </div>
                <div style={styles.emptyPill}>No data</div>
            </div>
        );
    }

    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader}>
                <span>{title}</span>
                <span style={styles.countBadge}>{values.length}</span>
            </div>
            <div style={styles.badgesWrap}>
                {values.map((value, index) => (
                    <span key={`${value}-${index}`} style={styles.dataBadge}>
                        {String(value)}
                    </span>
                ))}
            </div>
        </div>
    );
};

const styles = {
    root: {
        display: 'grid',
        gap: '14px',
    },
    emptyState: {
        border: '1px dashed #cbd5e1',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        color: '#475569',
        backgroundColor: '#f8fafc',
    },
    card: {
        border: '1px solid #dbe3ed',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        borderBottom: '1px solid #edf2f7',
        backgroundColor: '#f8fafc',
    },
    backendPill: {
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: '999px',
        color: '#1d4ed8',
        backgroundColor: '#dbeafe',
    },
    body: {
        display: 'grid',
        gap: '10px',
        padding: '12px',
    },
    keyValuesRow: {
        display: 'grid',
        gap: '8px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    },
    keyValueBox: {
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '8px 10px',
        backgroundColor: '#fdfefe',
    },
    keyLabel: {
        fontSize: '12px',
        color: '#64748b',
        marginBottom: '3px',
    },
    keyValue: {
        fontWeight: 600,
        color: '#0f172a',
        wordBreak: 'break-all',
    },
    section: {
        border: '1px solid #edf2f7',
        borderRadius: '10px',
        padding: '10px',
        backgroundColor: '#fcfdff',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        color: '#334155',
        fontWeight: 600,
        fontSize: '13px',
    },
    countBadge: {
        minWidth: '24px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 700,
        color: '#1e293b',
        backgroundColor: '#e2e8f0',
        borderRadius: '999px',
        padding: '2px 8px',
    },
    badgesWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
    },
    dataBadge: {
        fontSize: '12px',
        color: '#0f172a',
        backgroundColor: '#eaf1ff',
        border: '1px solid #cbdcfb',
        borderRadius: '8px',
        padding: '4px 8px',
        wordBreak: 'break-all',
    },
    emptyPill: {
        display: 'inline-block',
        fontSize: '12px',
        color: '#64748b',
        borderRadius: '999px',
        padding: '4px 10px',
        backgroundColor: '#f1f5f9',
        border: '1px solid #e2e8f0',
    },
};

export default class DisplayUsersInfoFromUserService extends ReactBrick {
    static getReactComponent($) {
        return () => {
            const data = useProperty($, 'data');
            const entries = toEntries(data);

            return (
                <div style={{height: '100%', overflow: 'auto'}}>
                    <div style={styles.root}>
                        {entries.length === 0 ? (
                            <div style={styles.emptyState}>No user info to display.</div>
                        ) : (
                            entries.map(([backendId, userInfo]) => (
                                <div key={backendId} style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <strong>User info</strong>
                                        <span style={styles.backendPill}>{backendId}</span>
                                    </div>
                                    <div style={styles.body}>
                                        <div style={styles.keyValuesRow}>
                                            <div style={styles.keyValueBox}>
                                                <div style={styles.keyLabel}>userId</div>
                                                <div style={styles.keyValue}>{userInfo?.userId || '-'}</div>
                                            </div>
                                            <div style={styles.keyValueBox}>
                                                <div style={styles.keyLabel}>backendId</div>
                                                <div style={styles.keyValue}>{userInfo?.backendId || backendId || '-'}</div>
                                            </div>
                                        </div>
                                        {renderList('onlineFrontendsRelatedToUser', userInfo?.onlineFrontendsRelatedToUser)}
                                        {renderList('userIsCheckingThesePRs', userInfo?.userIsCheckingThesePRs)}
                                        {renderList('frontendIds', userInfo?.frontendIds)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            );
        };
    }
}

registerBrick('019cd75754a0c1f5abc2', DisplayUsersInfoFromUserService);
